import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, categoryPageData } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'latest-links');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const pageData = categoryPageData[activeSlug] || categoryPageData['latest-links'];
  const articles = getArticlesByCategory(activeSlug);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageData.seoTitle,
    description: pageData.seoDescription,
    url: `https://91mitao.com/category/${activeSlug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: articles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://91mitao.com/article/${article.slug}`,
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={pageData.seoTitle}
        description={pageData.seoDescription}
        keywords={pageData.seoKeywords}
        canonicalUrl={`https://91mitao.com/category/${activeSlug}`}
      />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />

      <Navbar />

      <main className="pt-20">
        {/* Header */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory?.name || '分类' },
                ]}
              />
              <div className="mt-4">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {pageData.title}
                </h1>
                <p className="mt-2 text-sm text-foreground-600 max-w-2xl">
                  {pageData.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs + Content */}
        <section className="py-10 md:py-14">
          <div className="px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              {/* Category Switcher */}
              <div className="flex items-center gap-1.5 flex-wrap mb-8">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300/50 hover:text-primary-600'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {/* Articles List */}
              {articles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {articles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-300/50 transition-all cursor-pointer group"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                          {article.categoryName}
                        </span>
                        <span className="text-xs text-foreground-400">{article.date}</span>
                      </div>
                      <h3 className="font-semibold text-sm text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="text-[10px] px-2 py-0.5 rounded bg-background-100 text-foreground-500 whitespace-nowrap"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.views.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}